/**
 * React component for displaying content related to saving time on cleaning.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - Data containing information about the Less Time Cleaning section.
 * @returns {JSX.Element} React component.
 */

import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import styles from "./LessTimeCleaning.module.scss";
import lessTimeCleaning from "@assets/images/homepage/less_time_cleaning.webp";
import Image from "next/image";

function LessTimeCleaning({ data }) {
  const ANIMATED_TEXTS = Object.entries(
    data?.lessTimeCleaningAnimatedText?.fields || {}
  );

  const [bgColor, setBgColor] = useState("bg-teal");

  const bgColorMap = {
    0: "bg-teal",
    1: "bg-blue",
    2: "bg-olive",
    3: "bg-maroon",
  };

  return (
    <>
      <section className="grid-cols-18 xl:grid hidden lg:mt-[160px] max-w-[1440px] m-auto">
        <div className="col-span-1"></div>
        <div className="col-span-16 relative aspect-w-32 aspect-h-15">
          <div className="h-full w-full">
            <div className="h-full relative">
              <div className="w-[32.10%] absolute">
                <div className={`aspect-w-137 aspect-h-200`}>
                  <Image
                    src={lessTimeCleaning}
                    alt="Less Time Cleaning"
                    className="h-full w-full object-cover object-center rounded-[16px]"
                  />
                </div>
              </div>
              <div className="w-[66.01%] absolute right-0">
                <div className="aspect-w-169 aspect-h-120">
                  <div
                    className={`w-full h-full py-[56px] text-white rounded-[16px] mt-[16px] xl:mt-0 ${bgColor}`}
                  >
                    <h2
                      className={`pb-[1px] pr-[80px pl-[56px] ${styles.responsivePaddingLeft}`}
                    >
                      {data?.lessTimeCleaningMoreTimeTitle}
                    </h2>
                    <div className="homePageSwiper h-[80px]">
                      <Swiper
                        className="overflow-hidden"
                        spaceBetween={30}
                        effect={"fade-out"}
                        loop={true}
                        direction={"vertical"}
                        centeredSlides={false}
                        autoplay={{
                          delay: 3000,
                          disableOnInteraction: false,
                        }}
                        onSlideChange={(swiper) => {
                          setBgColor(bgColorMap[swiper.realIndex]);
                        }}
                        modules={[EffectFade, Autoplay]}
                      >
                        {ANIMATED_TEXTS.map(([key, value]) => (
                          <SwiperSlide key={key}>
                            <h2
                              className={`mt-[5px] py-[2px] pl-[56px] text-left ${styles.responsivePaddingLeft}`}
                            >
                              {value?.title}
                            </h2>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                    <div className="border-[0.5px] border-white50 mt-[70px]"></div>
                    <div className="flex flex-col pt-[70px]">
                      <div
                        className={`pr-[220px] pl-[56px] ${styles.responsivePaddingLeft}`}
                      >
                        {documentToReactComponents(
                          data?.lessTimeCleaningFooterRichText
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-1"></div>
      </section>

      <section className="xl:hidden block mt-[80px]">
        <div className="mx-[5.56%]">
          <div className="w-full h-full rounded-[16px]">
            <div className="aspect-w-327 aspect-h-218">
              <div className="h-full w-full">
                <div className="h-full relative">
                  <div className="w-full absolute">
                    <div className={`aspect-w-327 aspect-h-218`}>
                      <Image
                        src={lessTimeCleaning}
                        alt="Less Time Cleaning"
                        className="h-full w-full object-cover object-center rounded-[16px]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={`w-full h-full py-[32px] text-white rounded-8 mt-[16px] xl:mt-0 ${bgColor}`}
          >
            <h2 className="px-[32px] w-full">
              {data?.lessTimeCleaningMoreTimeTitle}
            </h2>

            <div className="homePageSwiper h-[80px]">
              <Swiper
                className="overflow-hidden"
                spaceBetween={30}
                effect={"fade-out"}
                loop={true}
                direction={"vertical"}
                centeredSlides={false}
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                onSlideChange={(swiper) => {
                  setBgColor(bgColorMap[swiper.realIndex]);
                }}
                modules={[EffectFade, Autoplay]}
              >
                {ANIMATED_TEXTS.map(([key, value]) => (
                  <SwiperSlide key={key}>
                    <h2
                      className="mt-[5px] py-[2px] px-[32px] w-full"
                      style={{ textAlign: "left" }}
                    >
                      {value?.title}
                    </h2>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <div className="border-[0.5px] border-white50 md:my-[2.67%] my-[32px]"></div>

            <div className="px-[32px] w-full">
              {documentToReactComponents(data?.lessTimeCleaningFooterRichText)}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default LessTimeCleaning;
